/**
 * Put text on the clipboard. Resolves true when it got there.
 *
 * The async Clipboard API is the first choice, but it is not guaranteed in an Office
 * webview: it needs a secure context and the `clipboard-write` permission, and hosts
 * differ in what they grant an add-in's iframe. So a refusal falls back to the old
 * select-and-execCommand route, which works from a click handler wherever the
 * page can select text. Neither path throws to the caller -- a failed copy is a
 * button that says so, not an error boundary.
 */
export const copyText = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Refused or unavailable; try the fallback below.
  }

  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    field.remove();
    return copied;
  } catch {
    return false;
  }
};
